import { useEffect, useState } from 'react';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import Button from '../../../components/Button';
import { UserLinks } from '../../../components/UserLinks';
import { journeyService } from '../services/journeyService';
import type { JourneyLeaderboardEntry } from '../services/journeyTypes';

const PAGE_SIZE = 20;

export const JourneyLeaderboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [entries, setEntries] = useState<JourneyLeaderboardEntry[]>([]);
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchLeaderboard = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await journeyService.getJourneyLeaderboard(PAGE_SIZE);
            setEntries(result.entries);
            setLastDoc(result.lastDoc);
            setHasMore(result.lastDoc !== null);
        } catch (err) {
            console.error('Error fetching journey leaderboard:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch leaderboard');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoadMore = async () => {
        if (!lastDoc || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const result = await journeyService.getJourneyLeaderboard(PAGE_SIZE, lastDoc);
            setEntries((prev) => [...prev, ...result.entries]);
            setLastDoc(result.lastDoc);
            setHasMore(result.lastDoc !== null);
        } catch (err) {
            console.error('Error loading more leaderboard entries:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchLeaderboard();
    }, []);

    const formatDate = (date: Date | null): string => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading leaderboard...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={fetchLeaderboard} variant="primary">
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Journey Leaderboard</h2>
                <Button onClick={fetchLeaderboard} variant="primary">
                    Refresh
                </Button>
            </div>

            {entries.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    No users with journey data found.
                </div>
            ) : (
                <>
                    <div className="mb-4 text-sm text-gray-600">
                        Showing top {entries.length} users by journey progress
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Rank
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Chapter
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Day
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Brain Points
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Energy
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Active
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {entries.map((entry, index) => (
                                    <tr
                                        key={entry.uid}
                                        className={index < 3 ? 'bg-yellow-50' : ''}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <span className={`text-sm font-bold ${index === 0 ? 'text-yellow-600 text-lg' :
                                                    index === 1 ? 'text-gray-500 text-lg' :
                                                        index === 2 ? 'text-orange-600 text-lg' :
                                                            'text-gray-900'
                                                    }`}>
                                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {entry.email || 'No email'}
                                            </div>
                                            {entry.displayName && (
                                                <div className="text-sm text-gray-500">
                                                    {entry.displayName}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                Ch {entry.currentChapterNumber}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-blue-600">
                                                Day {entry.dayNumber}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.usableBrainPoints}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {entry.energy}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(entry.updatedAt)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <UserLinks userId={entry.uid} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <div className="flex justify-center mt-6">
                            <Button onClick={handleLoadMore} variant="primary" disabled={isLoadingMore}>
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
